import type { ListView } from 'app/types/navigation'
import { preferredView, QueueView } from './boardView'
import { appendQueueNavSearch } from './locationParam'
import { queueViewPath } from './QueueViewToggle'
import { ActionQueueDef } from './queueRegistry'
import { SYSTEM_LIST_BY_QUEUE_ID, listPath } from 'lib/systemLists'

type LinkableQueue = Pick<ActionQueueDef, `id` | `surface` | `defaultView`>

/**
 * Which view a queue's card opens into.
 *
 * The one place that reads `surface` for navigation purposes, so a route and
 * its label can never end up disagreeing about where a click actually lands.
 * `board` queues have no focus flow to fall back to and always open the
 * board; `both` queues follow however this person last worked that queue,
 * falling back to the queue's own `defaultView` the first time; everything
 * else resolves in a single call and opens the one-at-a-time card.
 */
function queueView(def: LinkableQueue, userId: string | undefined): QueueView {
  if (def.surface === `board`) return `board`
  if (def.surface === `both`) return preferredView(userId, def.id, def.defaultView)
  return `focus`
}

/** The system list URL a queue's card should navigate through, band carried along, falling back to the queue id as its own list. */
function listTarget(def: LinkableQueue, view: ListView): string {
  const hit = SYSTEM_LIST_BY_QUEUE_ID.get(def.id)
  return hit ? listPath(hit.def.slug, view, hit.bandId) : listPath(def.id, view)
}

/**
 * Where a queue card's click should navigate to, location drill-down carried
 * over. With grouped navigation on, every view of a queue lives on its list
 * page; otherwise the legacy focus route or the Pipelines page.
 */
export function queueHref(
  def: LinkableQueue,
  userId: string | undefined,
  search: string,
  groupedNavigation = false,
): string {
  const view = queueView(def, userId)
  const path = groupedNavigation ? listTarget(def, view) : queueViewPath(view, def.id)
  return appendQueueNavSearch(path, search)
}

/** The list page's table, for the card's "View list" link once grouped navigation is on. */
export function listDrillHref(def: LinkableQueue): string {
  return listTarget(def, `table`)
}

/** The queue card's button label. Always names wherever `queueHref` actually goes. */
export function queueActionLabel(def: LinkableQueue, userId: string | undefined): string {
  return queueView(def, userId) === `board` ? `Open the board` : `Start working`
}
